import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Reusable pagination bar.
 *
 * Props:
 *  - page (1-based), pages (total pages), total (optional, for a nicer summary),
 *  - onChange(nextPage): called on any page nav
 *  - testIdPrefix: prefix for data-testids so multiple bars on one page don't collide
 */
const RANGE = 1; // pages to show on each side of the current page

function pageWindow(page, pages) {
  // Compact windowed list: 1 ... 4 5 [6] 7 8 ... 20
  const out = new Set([1, pages]);
  for (let p = page - RANGE; p <= page + RANGE; p++) {
    if (p >= 1 && p <= pages) out.add(p);
  }
  const sorted = [...out].sort((a, b) => a - b);
  const result = [];
  let prev = 0;
  for (const p of sorted) {
    if (prev && p - prev > 1) result.push('gap');
    result.push(p);
    prev = p;
  }
  return result;
}

const Pagination = ({ page = 1, pages = 1, total, onChange, testIdPrefix = 'pagination', className = '' }) => {
  if (!pages || pages <= 1) return null;
  const go = (p) => {
    if (p < 1 || p > pages || p === page) return;
    onChange && onChange(p);
  };
  const win = pageWindow(page, pages);
  return (
    <div className={`flex flex-wrap items-center justify-between gap-3 mt-6 ${className}`} data-testid={testIdPrefix}>
      <div className="text-xs" style={{ color: 'var(--jw-text-subtle)' }}>
        {total != null ? `Showing page ${page} of ${pages} · ${total} total` : `Page ${page} of ${pages}`}
      </div>
      <div className="flex items-center gap-1">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => go(page - 1)}
          disabled={page <= 1}
          data-testid={`${testIdPrefix}-prev-button`}
          className="h-8 px-2"
          aria-label="Previous page"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        {win.map((p, i) =>
          p === 'gap' ? (
            <span key={`gap-${i}`} className="px-1 text-xs" style={{ color: 'var(--jw-text-subtle)' }}>…</span>
          ) : (
            <button
              key={p}
              onClick={() => go(p)}
              data-testid={`${testIdPrefix}-page-${p}-button`}
              data-selected={p === page ? 'true' : 'false'}
              className="jw-chip"
              style={{ minWidth: '32px', padding: '4px 8px', fontSize: '12px' }}
            >
              {p}
            </button>
          )
        )}
        <Button
          size="sm"
          variant="ghost"
          onClick={() => go(page + 1)}
          disabled={page >= pages}
          data-testid={`${testIdPrefix}-next-button`}
          className="h-8 px-2"
          aria-label="Next page"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default Pagination;
